import type { ReactNode } from "react";

/**
 * One line inside the buy box's Cost Summary. Covers the three row shapes the
 * prototype styles (Campaign-Builder-Master-Prototype-Stage2-Revisions.html,
 * CSS ~lines 7738, 7763 and 7791):
 *
 * - plain `.checkout-cost-row` — Recipients, Budget per gift, Credits applied;
 * - `.checkout-cost-row.credits-to-purchase` — `topRule` + `strong` + a `note`
 *   under the label ("1 credit = $1");
 * - `.checkout-total-row` — `emphasis`, the big Crimson Estimated Total with
 *   its `(USD)` suffix and "Includes shipping & taxes" note.
 */
export interface CostSummaryRowProps {
  label: string;
  /** Money, or a bare count for the credit rows — this component never formats. */
  value: string;
  /** Small graphite line under the label. */
  note?: string;
  /** Lighter suffix after the label, e.g. "(USD)". */
  currency?: string;
  icon?: ReactNode;
  /** The big Crimson Estimated Total treatment. */
  emphasis?: boolean;
  /** Bold label and value — "Credits to purchase" and "Due today (40%)". */
  strong?: boolean;
  /** 1px card-border rule above the row, setting it off from the rows before it. */
  topRule?: boolean;
  /** Renders the value in Dark Dust — the deducted "Credits applied" line. */
  muted?: boolean;
}

export default function CostSummaryRow({
  label,
  value,
  note,
  currency,
  icon,
  emphasis = false,
  strong = false,
  topRule = false,
  muted = false,
}: CostSummaryRowProps) {
  if (emphasis) {
    return (
      <div className="flex items-center justify-between gap-3 pt-3.5 pb-1">
        <span className="flex flex-col font-lato text-base font-bold text-rodeo-black">
          <span>
            {label}
            {currency && <span className="ml-1 text-xs font-normal text-graphite">{currency}</span>}
          </span>
          {note && <span className="mt-0.5 text-[11px] font-normal text-graphite">{note}</span>}
        </span>
        <span className="font-crimson text-[28px] font-normal text-rodeo-black">{value}</span>
      </div>
    );
  }

  return (
    <div
      className={[
        "flex justify-between gap-3 py-1.5 font-lato text-sm text-rodeo-black",
        note ? "items-start" : "items-center",
        topRule ? "mt-1 border-t border-card-border pt-2.5" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className={note ? "flex flex-col gap-px" : "inline-flex items-center gap-1.5"}>
        <span className={strong ? "font-bold text-rodeo-black" : undefined}>
          {label}
          {icon}
        </span>
        {note && <span className="text-[11px] text-graphite">{note}</span>}
      </span>
      <span
        className={[
          strong ? "font-bold" : "font-semibold",
          muted ? "text-dust-ink" : "text-rodeo-black",
        ].join(" ")}
      >
        {value}
      </span>
    </div>
  );
}
